import React from 'react'
import styled from '@emotion/styled'
import { Spin } from 'antd'
import { BiSemanalStore } from './BiComparativoSemanal'

export function ComparativoWidget() {
  const { loading, periodo } = BiSemanalStore.useState()

  return (
    <WidgetCard>
      <WidgetTitle>
        {periodo === 'semana' ? 'Semana atual vs. média histórica' : 'Mês atual vs. média dos 6 meses anteriores'}
      </WidgetTitle>
      {loading ? (
        <Spin />
      ) : (
        <WidgetGrid>
          <MatriculasComparison />
          <ReceitaComparison />
        </WidgetGrid>
      )}
      <AverageNote />
    </WidgetCard>
  )
}

function AverageNote() {
  const { average, periodo } = BiSemanalStore.useState()
  if (!average) return null

  return (
    <WeeksNote>
      {periodo === 'semana'
        ? `Baseado em ${average.periodsUsed} semanas sem feriados ou outros eventos`
        : `Baseado em ${average.periodsUsed} meses, até o mesmo momento do mês`}
    </WeeksNote>
  )
}

function MatriculasComparison() {
  const { current: currentPeriod, average } = BiSemanalStore.useState()
  const current = currentPeriod?.countElapsed ?? null
  const avg = average?.count ?? null
  const delta = current !== null && avg !== null ? computeDelta(current, avg) : null

  return (
    <StatCard
      label="Matrículas"
      current={current !== null ? String(current) : '—'}
      average={avg !== null ? avg.toFixed(1) : '—'}
      delta={delta}
    />
  )
}

function ReceitaComparison() {
  const { current: currentPeriod, average } = BiSemanalStore.useState()
  const current = currentPeriod?.valorNewDriverSumElapsed ?? null
  const avg = average?.valorNewDriverSum ?? null
  const delta = current !== null && avg !== null ? computeDelta(current, avg) : null

  return (
    <StatCard
      label="Receita NewDriver"
      current={current !== null ? formatBRL(current) : '—'}
      average={avg !== null ? formatBRL(avg) : '—'}
      delta={delta}
    />
  )
}

interface Delta {
  absolute: number
  percent: number
  positive: boolean
}

function StatCard({
  label,
  current,
  average,
  delta,
}: {
  label: string
  current: string
  average: string
  delta: Delta | null
}) {
  return (
    <StatCardBox>
      <StatLabel>{label}</StatLabel>
      <StatCurrentValue>{current}</StatCurrentValue>
      <StatAverageRow>
        <StatAverageLabel>Média: </StatAverageLabel>
        <span>{average}</span>
      </StatAverageRow>
      {delta && (
        <DeltaBadge positive={delta.positive}>
          {delta.positive ? '+' : ''}
          {delta.percent.toFixed(1)}% ({delta.positive ? '+' : ''}
          {formatDeltaAbsolute(delta.absolute, label)})
        </DeltaBadge>
      )}
    </StatCardBox>
  )
}

function formatDeltaAbsolute(absolute: number, label: string): string {
  if (label === 'Matrículas') return String(Math.round(absolute))
  return formatBRL(absolute)
}

function computeDelta(current: number, avg: number): Delta {
  return {
    absolute: current - avg,
    percent: avg === 0 ? 0 : ((current - avg) / avg) * 100,
    positive: current >= avg,
  }
}

function formatBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

const WidgetCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
`

const WidgetTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #494d50;
  margin-bottom: 16px;
`

const WidgetGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`

const WeeksNote = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 8px;
`

const StatCardBox = styled.div`
  background: #fafafa;
  border-radius: 6px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StatLabel = styled.span`
  font-size: 12px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const StatCurrentValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  color: #494d50;
`

const StatAverageRow = styled.div`
  font-size: 13px;
  color: #666;
`

const StatAverageLabel = styled.span`
  font-weight: 500;
`

const DeltaBadge = styled.span<{ positive: boolean }>`
  display: inline-block;
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ positive }) => (positive ? '#389e0d' : '#cf1322')};
`
